import type { ReactNode } from 'react'
import { memo, useMemo } from 'react'
import { motion } from 'framer-motion'
import type { HTMLMotionProps } from 'framer-motion'

interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'premium' | 'glass' | 'outline' | 'gradient' | 'success' | 'warning'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  isLoading?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  children: ReactNode
}

const Button = memo(({ 
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  children,
  className = '',
  disabled,
  ...props 
}: ButtonProps) => {
  // Memoize static class configurations to prevent recreation on every render
  const baseClasses = useMemo(() => 
    'relative inline-flex items-center justify-center font-semibold rounded-2xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden',
    []
  )
  
  const variantClasses = useMemo(() => ({
    // Primary: Premium blue for main actions (WCAG AA: 4.5:1+ contrast)
    primary: 'bg-primary-600 text-white hover:bg-primary-700 hover:shadow-premium-glow focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-premium-md hover:shadow-premium-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200',
    
    // Secondary: Light neutral for secondary actions
    secondary: 'bg-neutral-100 text-neutral-800 hover:bg-neutral-200 hover:text-neutral-900 focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 border border-neutral-200 hover:border-neutral-300 shadow-premium-sm hover:shadow-premium transition-all duration-200',
    
    // Ghost: Minimal styling for subtle actions
    ghost: 'text-neutral-600 hover:text-neutral-800 hover:bg-neutral-50 focus:ring-2 focus:ring-neutral-400 focus:ring-offset-2 backdrop-blur-sm transition-all duration-200',
    
    // Danger: Red for destructive actions
    danger: 'bg-danger-600 text-white hover:bg-danger-700 hover:shadow-lg focus:ring-2 focus:ring-danger-500 focus:ring-offset-2 shadow-premium-md hover:shadow-premium-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200',
    
    // Premium: Gradient for premium actions (using 3-color system)
    premium: 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white hover:from-primary-700 hover:to-secondary-700 hover:shadow-premium-glow-lg focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-premium-lg hover:shadow-premium-xl transform hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden transition-all duration-200',
    
    // Glass: Translucent modern effect
    glass: 'bg-white/20 backdrop-blur-xl text-neutral-700 hover:bg-white/30 hover:text-neutral-800 focus:ring-2 focus:ring-white/50 focus:ring-offset-2 border border-white/20 hover:border-white/30 shadow-glass hover:shadow-premium-md transition-all duration-200',
    
    // Outline: Bordered primary style
    outline: 'border-2 border-primary-500 text-primary-600 hover:bg-primary-50 hover:text-primary-700 hover:border-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-premium-sm hover:shadow-premium transition-all duration-200',
    
    // Gradient: Premium purple-blue gradient
    gradient: 'bg-gradient-to-r from-secondary-600 to-primary-600 text-white hover:from-secondary-700 hover:to-primary-700 hover:shadow-premium-glow-lg focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 shadow-premium-lg hover:shadow-premium-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200',
    
    // Success: Green for positive actions
    success: 'bg-success-600 text-white hover:bg-success-700 hover:shadow-lg focus:ring-2 focus:ring-success-500 focus:ring-offset-2 shadow-premium-md hover:shadow-premium-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200',
    
    // Warning: Amber for warning actions
    warning: 'bg-warning-500 text-white hover:bg-warning-600 hover:shadow-lg focus:ring-2 focus:ring-warning-400 focus:ring-offset-2 shadow-premium-md hover:shadow-premium-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200'
  }), [])
  
  const sizeClasses = useMemo(() => ({
    xs: 'px-2.5 py-1.5 text-xs min-h-[32px]',
    sm: 'px-3 py-2 text-sm min-h-[36px]',
    md: 'px-4 py-2.5 text-base min-h-[44px]',
    lg: 'px-6 py-3 text-lg min-h-[52px]',
    xl: 'px-8 py-4 text-xl min-h-[60px]'
  }), [])

  // Memoize computed values
  const isDisabled = useMemo(() => disabled || isLoading, [disabled, isLoading])
  const buttonClassName = useMemo(() => 
    `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`,
    [baseClasses, variantClasses, sizeClasses, variant, size, className]
  )
  
  // Memoize animation configs to prevent recreation
  const hoverAnimation = useMemo(() => 
    !isDisabled ? { 
      scale: 1.02,
      y: -1
    } : {},
    [isDisabled]
  )
  
  const tapAnimation = useMemo(() =>
    !isDisabled ? { 
      scale: 0.98,
      y: 0
    } : {},
    [isDisabled]
  )
  
  const showShimmer = useMemo(() => 
    (variant === 'premium' || variant === 'gradient') && !isDisabled,
    [variant, isDisabled]
  )

  return (
    <motion.button
      className={buttonClassName}
      disabled={isDisabled}
      whileHover={hoverAnimation}
      whileTap={tapAnimation}
      {...props}
    >
      {/* Premium shimmer effect for premium variants */}
      {showShimmer && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
          initial={{ x: '-100%' }}
          animate={{ x: '100%' }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 3,
            ease: "easeInOut"
          }}
        />
      )}

      {isLoading ? (
        <div className="flex items-center">
          <motion.div 
            className="h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2" 
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <span>
            Cargando...
          </span>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2">
          {leftIcon && (
            <span className="flex items-center justify-center flex-shrink-0">
              {leftIcon}
            </span>
          )}
          <span className="flex items-center">
            {children}
          </span>
          {rightIcon && (
            <span className="flex items-center justify-center flex-shrink-0">
              {rightIcon}
            </span>
          )}
        </div>
      )}
    </motion.button>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function for memo - only re-render if relevant props changed
  return (
    prevProps.variant === nextProps.variant &&
    prevProps.size === nextProps.size &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.className === nextProps.className &&
    prevProps.children === nextProps.children &&
    prevProps.leftIcon === nextProps.leftIcon &&
    prevProps.rightIcon === nextProps.rightIcon
  );
});

Button.displayName = 'Button';

export default Button
export { Button }